import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  intervalToDuration,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { View } from './types';

// Format seconds to show only hours and minutes (no seconds)
// Always includes unit: "30m" or "1h30m"
export const secondsToHm = (seconds: number | null): string => {
  if (!seconds) return '';
  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
  const { hours = 0, minutes = 0 } = duration;

  let formattedDuration = '';

  if (hours > 0) {
    formattedDuration += `${hours}h`;
    if (minutes > 0) {
      formattedDuration += `${minutes}m`;
    }
  } else {
    // When no hours, show just minutes with unit (e.g., "30m")
    formattedDuration += `${minutes} minutes`;
  }

  return formattedDuration;
};

export const secondsToMinutes = (
  seconds: number | null,
  precision: number = 2
): number | null => {
  if (seconds === null || seconds === undefined) {
    return null;
  }
  const factor = 10 ** precision;
  return Math.round((seconds / 60) * factor) / factor;
};

// Returns an array of days, representing the days of the current week or month.
export const getCurrentViewDays = ({
  aday,
  view,
}: {
  aday?: Date;
  view: View;
}): Date[] => {
  // today is as good as any.
  if (!aday) aday = new Date();

  let fromDate: Date;
  let toDate: Date;
  if (view === View.Week) {
    fromDate = startOfWeek(new Date(aday), { weekStartsOn: 1 });
    toDate = endOfWeek(new Date(aday), { weekStartsOn: 1 });
  } else if (view === View.Month) {
    fromDate = startOfMonth(new Date(aday));
    toDate = endOfMonth(new Date(aday));
    // Adjust to include previous week from last Monday and next week till next Sunday
    fromDate = addDays(startOfWeek(fromDate, { weekStartsOn: 1 }), -7);
    toDate = addDays(endOfWeek(toDate, { weekStartsOn: 1 }), 7);
  } else {
    // default to daily items.
    fromDate = startOfDay(new Date(aday));
    toDate = endOfDay(new Date(aday));
  }

  // return the list of days between fromDate and toDate
  const days = [];
  let currentDate = fromDate;
  while (currentDate < toDate) {
    days.push(currentDate);
    currentDate = addDays(currentDate, 1);
  }
  return days;
};

export const formatTime = (value?: string | Date) => {
  const parsed = normalizeDateValue(value);
  return parsed ? format(parsed, 'HH:mm:ss') : '';
};

export const formatDateTime = (value?: string | Date) => {
  const parsed = normalizeDateValue(value);
  return parsed ? format(parsed, 'yyyy-MM-dd HH:mm:ss') : '';
};

const normalizeDateValue = (value?: string | Date): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  const trimmed = String(value).trim();
  const normalized = trimmed.replace(' ', 'T');
  const parsed = new Date(normalized);
  if (!isNaN(parsed.getTime())) return parsed;

  const fallback = new Date(trimmed);
  return isNaN(fallback.getTime()) ? null : fallback;
};
